import { useEffect, useMemo, useState } from "react";

import { SimpleTextareaResizable } from "@/Common/Components/Inputs/SimpleTextareaResizable";
import { TextboxControl } from "@/Common/Components/Inputs/TextboxControl";
import { TextboxMaskedControl } from "@/Common/Components/Inputs/TextboxMaskedControl";
import { usePartialState } from "@/Common/CustomHooks/usePartialState";
import { useTranslation } from "@/Common/CustomHooks/useTranslation";
import { ValidationHelper } from "@/Common/Helpers/ValidationHelper";
import { MedfarDictionariesValidationRuleTypeEnum } from "@/Common/Models/MedfarDictionaries/Enums/MedfarDictionariesValidationRuleTypeEnum";
import { KeyValuePair } from "@/Common/Models/UI/KeyValuePair";
import { ContactDirectoryHelper } from "@/ContactDirectory/Helpers/ContactDirectoryHelper";
import { MedfarDictionariesValidationHelper } from "@/ContactDirectory/Helpers/MedfarDictionariesValidationHelper";
import { useDefaultDirectoryCountryAndState } from "@/ContactDirectory/Hooks/useDefaultDirectoryCountryAndState";
import { useMedfarDictionariesValidationRule } from "@/ContactDirectory/Hooks/useMedfarDictionariesValidationRule";
import { DirectoryAddress } from "@/ContactDirectory/Models//DirectoryAddress";
import { DirectoryFaxNumber } from "@/ContactDirectory/Models//DirectoryFaxNumber";
import { DirectoryOtherEstablishment } from "@/ContactDirectory/Models//DirectoryOtherEstablishment";
import { DirectoryPhoneNumber } from "@/ContactDirectory/Models//DirectoryPhoneNumber";
import { DuplicateSearchType } from "@/ContactDirectory/Models/enums/DuplicateSearchType";
import { OtherEstablishmentValidator } from "@/ContactDirectory/Validators/OtherEstablishmentValidator";
import { AddressFormSection } from "../Common/AddressFormSection";
import { DirectoryContactNumber } from "../Common/DirectoryContactNumber";
import { DirectoryHeader } from "../Common/DirectoryHeader";
import { DirectoryOthersNamingRow } from "../Common/DirectoryOthersNamingRow";
import { DisplacedDirectoryFormRow } from "../Common/DisplacedDirectoryFormRow";
import { DuplicateWarningPopup } from "../DuplicatePopup/DuplicateWarningPopup";
import { IDirectoryFormProps, IDirectoryFormRawInformation } from "../IDirectoryFormProps";

import commonStyles from "../FaxDirectoryForm.module.scss";

const initializeModel = (editModel: DirectoryOtherEstablishment | null, countryId: string, stateId: string, rawInfo?: IDirectoryFormRawInformation): DirectoryOtherEstablishment => {
    if (editModel)
        return editModel;

    const clinicAddress = ContactDirectoryHelper.parseClinicNameStreetAddress(rawInfo?.rawAddress);

    return new DirectoryOtherEstablishment({
        name: clinicAddress?.clinicName,
        address: new DirectoryAddress({ countryId, stateId, streetAddress: clinicAddress?.address ?? "" }),
        faxNumbers: [new DirectoryFaxNumber({ countryId, ...rawInfo?.faxNumber })],
        phoneNumbers: rawInfo?.rawPhoneNumber ? [new DirectoryPhoneNumber({ number: rawInfo?.rawPhoneNumber, countryId: rawInfo?.activeSiteCountryId })] : []
    });
};

export function OtherEstablishmentForm(props: IDirectoryFormProps<DirectoryOtherEstablishment, DirectoryOtherEstablishment>): JSX.Element {
    const { getText } = useTranslation();
    const activeSiteCountryId = useMemo(() => cacheStore.getCountryMedfarDictionariesByLookUpCode(cacheStore.GetDefaultCountryForActiveSite().LookupCode)?.Id, []);

    const [isDuplicate, setIsDuplicate] = useState(false);
    const [isFaxCountryChanged, setIsFaxCountryChanged] = useState<{ [index: number]: boolean }>({});
    const [isPhoneCountryChanged, setIsPhoneCountryChanged] = useState<{ [index: number]: boolean }>({});
    const { defaultCountry, defaultState } = useDefaultDirectoryCountryAndState();
    const [entity, setEntityState] = usePartialState<DirectoryOtherEstablishment>(
        () => initializeModel(props.editModel, defaultCountry.Id, defaultState.Id, {
            ...props.rawInformation,
            activeSiteCountryId
        }),
        s => new DirectoryOtherEstablishment(s)
    );

    function setEntity(updatedFields: Partial<DirectoryOtherEstablishment>): void {
        const newState = setEntityState(updatedFields);
        props.onModelUpdate(newState);
    }

    const countries = useMemo(
        () => ContactDirectoryHelper.getSupportedCountries(),
        []
    );

    const countryOptions = useMemo(
        () => countries.map(p => new KeyValuePair(p.Id, p.Name)),
        [countries]
    );

    function setFax(faxNumber: Partial<DirectoryFaxNumber>, index: number): void {
        const faxes = [...entity.faxNumbers];
        faxes[index] = new DirectoryFaxNumber({ ...faxes[index], ...faxNumber });
        setEntity({ faxNumbers: faxes });
    }

    function setPhone(phoneNumber: Partial<DirectoryPhoneNumber>, index: number): void {
        const phoneNumbers = [...entity.phoneNumbers];
        phoneNumbers[index] = new DirectoryPhoneNumber({ ...phoneNumbers[index], ...phoneNumber });
        setEntity({ phoneNumbers: phoneNumbers });
    }

    const setAddress = (address: Partial<DirectoryAddress>): DirectoryAddress => {
        const newAddress = new DirectoryAddress({ ...entity.address, ...address });

        const faxNumbers = entity.faxNumbers.map((p, i) => {
            if ((validationResult.faxNumbers[i].isValid && !validationResult.faxNumbers[i].isDuplicateInForm) || isFaxCountryChanged[i])
                return p;

            return new DirectoryFaxNumber({ ...p, countryId: newAddress.countryId });
        });

        const phoneNumbers = entity.phoneNumbers.map((p, i) => {
            if ((validationResult.phoneNumbers[i].isValid && !validationResult.phoneNumbers[i].isDuplicateInForm) || isPhoneCountryChanged[i])
                return p;

            return new DirectoryPhoneNumber({ ...p, countryId: newAddress.countryId });
        });

        setEntity({
            faxNumbers: faxNumbers,
            phoneNumbers: phoneNumbers,
            address: newAddress
        });

        return newAddress;
    };

    // Validation
    const validationRuleZipCode = useMedfarDictionariesValidationRule(MedfarDictionariesValidationRuleTypeEnum.PostalCode, entity.address.countryId, entity.address.stateId);
    const validationRulePhone = useMedfarDictionariesValidationRule(MedfarDictionariesValidationRuleTypeEnum.PhoneNumber, entity.address.countryId, entity.address.stateId);
    const validationRuleEmail = useMemo(() => new ValidationHelper().getEmailValidationRule(), []);

    const validator = useMemo(
        () => new OtherEstablishmentValidator(
            new MedfarDictionariesValidationHelper(cacheStore.MedfarDictionariesValidationRules),
            cacheStore.CountryCache,
            ValidationHelper.emailRegex
        ),
        []
    );

    const validationResult = useMemo(
        () => validator.validate(entity),
        [validator, entity]
    );

    // Parent updates
    useEffect(() => props.onValidityChange(validationResult.isFormValid && !isDuplicate), [validationResult.isFormValid, isDuplicate]);

    useEffect(
        () => {
            setIsDuplicate(false);

            if (IsNullOrWhiteSpace(entity.name))
                return;

            const timeout = setTimeout(
                async () => setIsDuplicate(
                    await props.checkIfDisallowedDuplicate({ name: entity.name, exceptOtherEstablishmentId: entity.id })
                ),
                500
            );

            return () => clearTimeout(timeout);
        },
        [entity.id, entity.name]
    );

    return (
        <div>
            {props.showWarningDuplicateMessage && (
                <DuplicateWarningPopup
                    {...props.duplicateWarningPopupProps}
                    onModify={entity => {
                        setEntity(entity);
                        props.duplicateWarningPopupProps.onModify?.(entity);
                        props.duplicateWarningPopupProps.onClose();
                    }}
                    onUsedInstead={entity => {
                        props.onEntityUseInstead(entity);
                        props.duplicateWarningPopupProps.onUsedInstead?.(entity);
                        props.duplicateWarningPopupProps.onClose();
                    }}
                />
            )}
            <DirectoryOthersNamingRow
                labelKey="NameLabel"
                othersNamingInstruction="ContactDirectory_OthersNamingInstructionLabel"
                othersNamingStructure="ContactDirectory_OthersNamingStructureLabel"
                className={commonStyles.removeableRightSpacing}
            >
                <TextboxControl
                    value={entity.name}
                    onChange={name => setEntity({ name })}
                    placeholder={getText("ContactDirectory_OthersNamingPlaceholderLabel")}
                    valid={validationResult.name && !isDuplicate}
                    maxLength={250}
                    onBlur={() => {
                        if (validationResult.name)
                            props.searchDuplicateForWarning(entity, entity.name, DuplicateSearchType.Name);
                    }}
                />
                {isDuplicate && (
                    <div className="red-text margin-xs-top">
                        {getText("FieldCannotBeReusedInTwoEntriesLabel", { field: getText("TheNameLabel") })}
                    </div>
                )}
            </DirectoryOthersNamingRow>
            <AddressFormSection
                address={entity.address}
                setAddress={address => setAddress(address)}
                validationResult={validationResult.address}
                validationZipCode={validationRuleZipCode}
                containerClassName={commonStyles.removeableRightSpacing}
                searchDuplicateForWarning={address => {
                    if (!IsNullOrWhiteSpace(entity.address.streetAddress) && !IsNullOrWhiteSpace(entity.address?.city) && validationResult.address.isFormValid)
                        props.searchDuplicateForWarning(entity, address, DuplicateSearchType.Address);
                }}
            />
            <DirectoryHeader
                labelKey="FaxNumbersLabel"
                onAdd={() => validationResult.faxNumbers.all(p => p.isValid && !p.isDuplicateInForm)
                    && setEntity({
                        faxNumbers: [
                            ...entity.faxNumbers,
                            new DirectoryFaxNumber({ countryId: entity.address.countryId || activeSiteCountryId })
                        ]
                    })
                }
                className={commonStyles.removeableRightSpacing}
                shouldDisplayAddLabel={validationResult.faxNumbers.all(p => p.isValid && !p.isDuplicateInForm)}
            />
            {entity.faxNumbers.map((p, i) => (
                <DirectoryContactNumber
                    key={p.id}
                    id={`faxNumber-${p.id}`}
                    number={p.number}
                    isFax={true}
                    onNumberChange={number => setFax({ number }, i)}
                    countryOptions={countryOptions}
                    selectedCountryId={p.countryId}
                    onSelectCountryId={countryId => {
                        setFax({ countryId }, i);
                        setIsFaxCountryChanged({ ...isFaxCountryChanged, [i]: true });
                    }}
                    isValid={validationResult.faxNumbers[i].isValid && !validationResult.faxNumbers[i].isDuplicateInForm}
                    description={p.description}
                    onDescriptionChange={description => setFax({ description }, i)}
                    validationRule={validationRulePhone}
                    onRemove={entity.faxNumbers.length > 1
                        ? () => {
                            const faxNumbers = [...entity.faxNumbers];
                            faxNumbers.splice(i, 1);
                            setEntity({ faxNumbers });
                            setIsFaxCountryChanged({ ...isFaxCountryChanged, [i]: false });
                        }
                        : null
                    }
                    onCheckForDuplicates={() => {
                        if (validationResult.faxNumbers[i].isValid && !validationResult.faxNumbers[i].isDuplicateInForm)
                            props.searchDuplicateForWarning(entity, p, DuplicateSearchType.FaxNumber);
                    }}
                    isDuplicateInForm={validationResult.faxNumbers[i].isDuplicateInForm}
                    addRightSpacing={entity.faxNumbers.length == 1}
                />
            ))}
            <DirectoryHeader
                labelKey="PhoneNumbersLabel"
                onAdd={() => validationResult.phoneNumbers.all(p => p.isValid && !p.isDuplicateInForm)
                    && setEntity({
                        phoneNumbers: [
                            ...entity.phoneNumbers,
                            new DirectoryPhoneNumber({ countryId: entity.address.countryId || activeSiteCountryId })
                        ]
                    })
                }
                className={commonStyles.removeableRightSpacing}
                shouldDisplayAddLabel={validationResult.phoneNumbers.all(p => p.isValid && !p.isDuplicateInForm)}
            />
            {entity.phoneNumbers.map((p, i) => (
                <DirectoryContactNumber
                    key={p.id}
                    id={`phoneNumber-${p.id}`}
                    number={p.number}
                    isFax={false}
                    isValid={validationResult.phoneNumbers[i].isValid && !validationResult.phoneNumbers[i].isDuplicateInForm}
                    onNumberChange={number => setPhone({ number }, i)}
                    extension={p.extension}
                    onExtensionChange={extension => setPhone({ extension }, i)}
                    countryOptions={countryOptions}
                    selectedCountryId={p.countryId}
                    onSelectCountryId={countryId => {
                        setPhone({ countryId }, i);
                        setIsPhoneCountryChanged({ ...isPhoneCountryChanged, [i]: true });
                    }}
                    validationRule={validationRulePhone}
                    onRemove={() => {
                        const phoneNumbers = [...entity.phoneNumbers];
                        phoneNumbers.splice(i, 1);
                        setEntity({ phoneNumbers });
                        setIsPhoneCountryChanged({ ...isPhoneCountryChanged, [i]: false });
                    }}
                    isDuplicateInForm={validationResult.phoneNumbers[i].isDuplicateInForm}
                />
            ))}
            <DirectoryHeader labelKey="OtherInformationLabel" className={commonStyles.removeableRightSpacing} />
            <DisplacedDirectoryFormRow labelKey="EmailLabel" className={commonStyles.removeableRightSpacing}>
                <TextboxMaskedControl
                    isValid={validationResult.email}
                    id="number-other-establishment-email"
                    allowEmpty
                    isEmail
                    returnMaskedValue
                    value={entity.email}
                    onChangeFunction={email => setEntity({ email })}
                    mask={validationRuleEmail.EditMaskPattern}
                    maxLength={250}
                />
            </DisplacedDirectoryFormRow>
            <DisplacedDirectoryFormRow labelKey="ResponsiblePersonsNameLabel" className={commonStyles.removeableRightSpacing}>
                <TextboxControl
                    value={entity.responsiblePersonName}
                    onChange={responsiblePersonName => setEntity({ responsiblePersonName })}
                    maxLength={400}
                />
            </DisplacedDirectoryFormRow>
            <DisplacedDirectoryFormRow labelKey="NotesLabel" className={commonStyles.removeableRightSpacing}>
                <SimpleTextareaResizable
                    value={entity.note}
                    onChange={(e: any) => {
                        const note = e.target.value;
                        setEntity({ note });
                    }}
                    maxLength={3000}
                />
            </DisplacedDirectoryFormRow>
        </div>
    );
}
